import { sectionsBlockSaveForAiInstructions } from '../../../../BriefTemplateBuilder.schema';
import { resolveSectionAiInstructions, type SectionDraft } from '../../../../lib/sectionDraft';

export interface UseBriefSectionDrawerReturn {
  /**
   * `resolveSectionAiInstructions` rendered: while `aiInstructions` is null the drawer shows
   * `instructions` instead. Nothing is written by showing it — typing is still the only thing
   * that sets `aiInstructions`, and closing without typing leaves it null. The Save gate and
   * the outgoing payload resolve the same way, so what is shown is what is judged and sent.
   */
  displayedAiInstructions: string;
  /**
   * Whether THIS section is what would block a save. Delegates to the page's own gate rather
   * than restating it, so the drawer and the gate cannot drift apart again.
   */
  aiInstructionsInvalid: boolean;
  handleNameChange: (next: string) => void;
  handleRequiredChange: (next: boolean) => void;
  handleAiAllowedChange: (next: boolean) => void;
  handleInstructionsChange: (next: string) => void;
  handleAiInstructionsChange: (next: string) => void;
  handleAllowFilesChange: (next: boolean) => void;
  handleConfigChange: (patch: Record<string, unknown>) => void;
}

export function useBriefSectionDrawer(
  section: SectionDraft,
  /**
   * This section as it is currently persisted, or `undefined` when it is new. The gate
   * grandfathers a section nobody touched, and it can only tell that by comparing against
   * this — without it the drawer flags the entire pre-existing estate, every one of which
   * saves fine.
   */
  persistedSection: SectionDraft | undefined,
  onPatch: (patch: Partial<SectionDraft>) => void,
): UseBriefSectionDrawerReturn {
  const displayedAiInstructions = resolveSectionAiInstructions(section) ?? '';

  // The page's gate, asked about this one section, rather than a second predicate that has to
  // be kept in step with it. Judging the field without the persisted snapshot flagged every
  // untouched legacy section; delegating is what makes the drawer and the page agree.
  const aiInstructionsInvalid = sectionsBlockSaveForAiInstructions(
    [section],
    persistedSection ? [persistedSection] : [],
  );

  return {
    displayedAiInstructions,
    aiInstructionsInvalid,
    handleNameChange: (next) => onPatch({ name: next }),
    handleRequiredChange: (next) => onPatch({ required: next }),
    handleAiAllowedChange: (next) => onPatch({ aiAllowed: next }),
    handleInstructionsChange: (next) => onPatch({ instructions: next }),
    handleAiInstructionsChange: (next) => onPatch({ aiInstructions: next }),
    handleAllowFilesChange: (next) => onPatch({ allowFiles: next }),
    handleConfigChange: (patch) => onPatch({ config: { ...section.config, ...patch } }),
  };
}
